import { useState } from 'react'
import { Link } from 'react-router-dom'
import ProjectFollowButton from './ProjectFollowButton'
import DiscountBadge from './DiscountBadge'

export default function ProjectCard({ project, onLikeClick, isReserved, showLikeButton }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  const images = project.image_urls?.filter(Boolean) || []
  const hasMultipleImages = images.length > 1

  const minSwipeDistance = 50

  const onTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && currentImageIndex < images.length - 1) {
      setCurrentImageIndex(prev => prev + 1)
    }
    if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1)
    }
  }

  const handlePrevImage = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1)
    }
  }

  const handleNextImage = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex(prev => prev + 1)
    }
  }

  const handleDotClick = (e, index) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex(index)
  }

  const unitsByType = {}
  if (project.units) {
    project.units.forEach(unit => {
      if (unit.status === 'draft') return

      const key = `${unit.bedrooms}bed-${unit.bathrooms}bath`
      if (!unitsByType[key]) {
        unitsByType[key] = {
          bedrooms: unit.bedrooms,
          bathrooms: unit.bathrooms,
          prices: [],
          availableCount: 0,
          totalCount: 0
        }
      }

      unitsByType[key].totalCount++
      if (unit.status === 'available') {
        unitsByType[key].availableCount++
      }
      if (unit.price) {
        unitsByType[key].prices.push(unit.price)
      }
    })
  }

  const sortedTypes = Object.values(unitsByType).sort((a, b) => {
    if (a.bedrooms !== b.bedrooms) return a.bedrooms - b.bedrooms
    return a.bathrooms - b.bathrooms
  })

  return (
    <Link to={`/projects/${project.id}`}>
      <div className="bg-white md:card-hover overflow-hidden group relative md:rounded-xl border-b md:border-b-0 border-secondary-100 md:border-none">
        {/* Image Carousel - Full width on mobile, rounded on desktop */}
        <div
          className="relative w-full aspect-[16/9] lg:aspect-[21/9] bg-secondary-200 overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Image */}
          {images.length > 0 ? (
            <img
              src={images[currentImageIndex]}
              alt={project.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          )}

          {/* Navigation Arrows - Desktop Only */}
          {hasMultipleImages && (
            <>
              <button
                onClick={handlePrevImage}
                className={`hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-white/80 hover:bg-white rounded-full shadow-lg transition-all ${
                  currentImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
                }`}
                disabled={currentImageIndex === 0}
              >
                <svg className="w-6 h-6 text-secondary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={handleNextImage}
                className={`hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-white/80 hover:bg-white rounded-full shadow-lg transition-all ${
                  currentImageIndex === images.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
                }`}
                disabled={currentImageIndex === images.length - 1}
              >
                <svg className="w-6 h-6 text-secondary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image Dots */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => handleDotClick(e, index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentImageIndex
                      ? 'bg-white w-6'
                      : 'bg-white/60 hover:bg-white/80'
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Status Badge - Desktop */}
          <div className="hidden lg:block absolute bottom-4 left-4">
            <span className={`badge ${
              project.status === 'activated' ? 'badge-success' :
              project.status === 'coming_soon' ? 'badge-warning' :
              'badge-primary'
            }`}>
              {project.status === 'coming_soon' ? 'Coming Soon' :
               project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>
          </div>

          {/* Bottom Right Badges - Stacked Vertically */}
          <div className="absolute bottom-4 right-4 z-10 flex flex-col items-end gap-2">
            {/* Instagram-style Discount Badge - Above */}
            {project.current_discount && (
              <DiscountBadge
                discountPercent={parseFloat(project.current_discount.discount_percent)}
                size="md"
              />
            )}

            {/* Instagram-style Follow Button - Below */}
            {showLikeButton && (
              <ProjectFollowButton
                isFollowing={project.is_following}
                onClick={(e) => onLikeClick(e, project)}
                size="md"
              />
            )}
          </div>
        </div>

        {/* Content - Desktop */}
        <div className="hidden lg:block p-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <h3 className="text-2xl font-semibold text-secondary-900 flex-1">
              {project.name}
            </h3>
          </div>

          <p className="text-secondary-600 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {project.location}
          </p>

          {project.price_range_min && project.price_range_max && (
            <p className="text-xl font-semibold text-primary-600 mb-3">
              ${project.price_range_min.toLocaleString()} - ${project.price_range_max.toLocaleString()}
            </p>
          )}

          {project.developer && (
            <p className="text-sm text-secondary-500 mb-4">
              By {project.developer.company_name || project.developer.full_name}
            </p>
          )}

          {/* Unit Type Summary */}
          {sortedTypes.length > 0 && (
            <div className="border-t border-secondary-200 pt-4 mt-4">
              <h4 className="text-xs font-semibold text-secondary-700 uppercase tracking-wide mb-3">
                Available Unit Types
              </h4>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {sortedTypes.slice(0, 4).map((type, idx) => (
                  <div key={idx} className="bg-secondary-50 rounded p-2 border border-secondary-200">
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <div className="flex items-center gap-1 text-secondary-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 20v-8a2 2 0 012-2h14a2 2 0 012 2v8m-18 0h18M5 10V6a2 2 0 012-2h10a2 2 0 012 2v4M7 14h2m4 0h2" />
                        </svg>
                        <span className="font-semibold">{type.bedrooms}</span>
                      </div>
                      <span className="text-secondary-400">•</span>
                      <div className="flex items-center gap-1 text-secondary-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 4h.01M4 16h16a1 1 0 001-1V8H3v7a1 1 0 001 1zm0 0v2a2 2 0 002 2h1m-3-4v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                        </svg>
                        <span className="font-semibold">{type.bathrooms}</span>
                      </div>
                    </div>
                    <div className={`text-xs font-medium ${
                      type.availableCount > 0
                        ? 'text-green-700'
                        : 'text-secondary-500'
                    }`}>
                      {type.availableCount} available
                    </div>
                    {type.prices.length > 0 && (
                      <div className="text-xs text-secondary-600 mt-1">
                        {Math.min(...type.prices) === Math.max(...type.prices)
                          ? `$${Math.min(...type.prices).toLocaleString()}`
                          : `$${Math.min(...type.prices).toLocaleString()}+`
                        }
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {sortedTypes.length > 4 && (
                <p className="text-xs text-secondary-500 mt-2">
                  +{sortedTypes.length - 4} more unit type{sortedTypes.length - 4 > 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Content - Mobile (Collapsible) */}
        <div className="lg:hidden">
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary-50 transition-colors"
          >
            <div className="flex-1 text-left">
              <h3 className="text-lg font-semibold text-secondary-900">
                {project.name}
              </h3>
              <p className="text-sm text-secondary-600 mt-1">
                {project.location}
              </p>
            </div>
            <svg
              className={`w-5 h-5 text-secondary-600 transition-transform flex-shrink-0 ml-4 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isExpanded && (
            <div className="px-4 pb-4 border-t border-secondary-200">
              <div className="pt-4 space-y-3">
                {project.price_range_min && project.price_range_max && (
                  <p className="text-lg font-semibold text-primary-600">
                    ${project.price_range_min.toLocaleString()} - ${project.price_range_max.toLocaleString()}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  {project.developer && (
                    <p className="text-sm text-secondary-500">
                      By {project.developer.company_name || project.developer.full_name}
                    </p>
                  )}
                  <span className={`badge ${
                    project.status === 'activated' ? 'badge-success' :
                    project.status === 'coming_soon' ? 'badge-warning' :
                    'badge-primary'
                  }`}>
                    {project.status === 'coming_soon' ? 'Coming Soon' :
                     project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </span>
                </div>

                {sortedTypes.length > 0 && (
                  <div className="pt-3 border-t border-secondary-200">
                    <h4 className="text-xs font-semibold text-secondary-700 uppercase tracking-wide mb-2">
                      Available Unit Types
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {sortedTypes.slice(0, 4).map((type, idx) => (
                        <div key={idx} className="bg-secondary-50 rounded p-2 border border-secondary-200">
                          <div className="flex items-center gap-2 text-xs mb-1">
                            <div className="flex items-center gap-1 text-secondary-700">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 20v-8a2 2 0 012-2h14a2 2 0 012 2v8m-18 0h18M5 10V6a2 2 0 012-2h10a2 2 0 012 2v4M7 14h2m4 0h2" />
                              </svg>
                              <span className="font-semibold">{type.bedrooms}</span>
                            </div>
                            <span className="text-secondary-400">•</span>
                            <div className="flex items-center gap-1 text-secondary-700">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 4h.01M4 16h16a1 1 0 001-1V8H3v7a1 1 0 001 1zm0 0v2a2 2 0 002 2h1m-3-4v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                              </svg>
                              <span className="font-semibold">{type.bathrooms}</span>
                            </div>
                          </div>
                          <div className={`text-xs font-medium ${
                            type.availableCount > 0
                              ? 'text-green-700'
                              : 'text-secondary-500'
                          }`}>
                            {type.availableCount} available
                          </div>
                          {type.prices.length > 0 && (
                            <div className="text-xs text-secondary-600 mt-1">
                              {Math.min(...type.prices) === Math.max(...type.prices)
                                ? `$${Math.min(...type.prices).toLocaleString()}`
                                : `$${Math.min(...type.prices).toLocaleString()}+`
                              }
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}
